import React, { useCallback, useEffect, useState } from "react";
import { Truck, Leaf, TreePine, Shirt, Gift } from "lucide-react";
import { StockingBuilder } from "@/components/StockingBuilder";
import { StockingSpotlight } from "@/components/StockingSpotlight";
import { getProducts } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

const SectionHeading = ({ id, icon: Icon, title, subtitle, color }) => (
  <div className="mb-8 text-center" data-testid={`${id}-heading`}>
    <h2 className={`font-spooky text-4xl sm:text-6xl ${color} drop-shadow-[3px_3px_0_#000] flex items-center justify-center gap-3`}>
      <Icon className="h-8 w-8 sm:h-10 sm:w-10 shrink-0" /> {title}
    </h2>
    <p className="mt-2 font-heading font-600 text-candy-cream/80 text-base">{subtitle}</p>
  </div>
);

const Grid = ({ items, data }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
    {items.map((p, i) => (
      <ProductCard key={p.id} product={p} sizes={data.merch_sizes} colors={data.merch_colors} index={i} />
    ))}
  </div>
);

export const Shop = () => {
  const [data, setData] = useState({ products: [], merch_sizes: [], merch_colors: [], shop_open: false });
  const [loading, setLoading] = useState(true);
  const [stockingOpen, setStockingOpen] = useState(false);

  const load = useCallback(() => {
    getProducts().then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    window.addEventListener("shop-opened", load);
    return () => window.removeEventListener("shop-opened", load);
  }, [load]);

  const byCat = (c) => data.products.filter((p) => p.category === c && !p.custom);
  const stocking = data.products.find((p) => p.stocking);

  return (
    <section id="shop" className="py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div
          data-testid="shop-shipping-notice"
          className="mb-14 flex items-start sm:items-center gap-3 bg-candy-gold text-black border-4 border-black rounded-2xl shadow-brutal p-4 sm:p-5"
        >
          <div className="bg-candy-pinkdark border-4 border-black rounded-xl p-2.5 shrink-0">
            <Truck className="h-6 w-6 text-white" />
          </div>
          <p className="font-heading font-bold text-sm sm:text-base leading-snug">
            We ship <b>every Friday before 12:00 PM CST</b>. Expedited orders must be requested at least <b>48 hours in advance</b>.
          </p>
        </div>

        {loading ? (
          <p className="font-mono text-sm text-candy-cream/70">Loading the good stuff…</p>
        ) : (
          <div className="space-y-20">
            <div id="shop-fall" className="scroll-mt-24" data-testid="fall-collection">
              <SectionHeading id="fall" icon={Leaf} title="Fall Candy Bouquets" color="text-candy-gold"
                subtitle={data.shop_open ? "Cozy autumn bouquets, made to order." : "Coming soon — orders open November 1."} />
              <Grid items={byCat("fall")} data={data} />
            </div>

            <div id="shop-christmas" className="scroll-mt-24" data-testid="christmas-collection">
              <SectionHeading id="christmas" icon={TreePine} title="Christmas Candy Bouquets" color="text-candy-pink"
                subtitle={data.shop_open ? "Every bouquet includes stocking stuffer treats." : "Coming soon — every bouquet includes stocking stuffer treats."} />
              <Grid items={byCat("christmas")} data={data} />
            </div>

            <div id="shop-stuffers" className="scroll-mt-24" data-testid="stuffers-collection">
              <SectionHeading id="stuffers" icon={Gift} title="Stocking Stuffers" color="text-candy-lime"
                subtitle={data.shop_open ? "Little treats for every stocking." : "Coming soon — little treats for every stocking."} />
              {stocking && <StockingSpotlight stocking={stocking} picks={data.stocking_picks} onOpen={() => setStockingOpen(true)} />}
              <Grid items={byCat("stuffers")} data={data} />
            </div>

            <div id="shop-merch" className="scroll-mt-24 pt-10 border-t-4 border-dashed border-candy-teal/50" data-testid="merch-collection">
              <SectionHeading id="merch" icon={Shirt} title="Bandit Merch" color="text-candy-lime"
                subtitle="Available now — pick your size & color." />
              <Grid items={byCat("merch")} data={data} />
            </div>
          </div>
        )}
      </div>
      <StockingBuilder open={stockingOpen} onClose={() => setStockingOpen(false)} stocking={stocking} stuffers={byCat("stuffers")} picks={data.stocking_picks} />
    </section>
  );
};
